/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../App";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";
import {
  Upload, Download, Trash2, FileText, Paperclip, AlertTriangle, Eye,
} from "lucide-react";
import DocumentViewer from "./DocumentViewer";
import { mensagemDeErro } from "../lib/erroApi";
import { copiaDoFicheiro } from "../lib/copiaDoFicheiro";


// Categorias por tipo de pessoa. Os documentos marcados em OBRIGATORIOS têm de ter validade: é ela que controla os avisos.
const CATEGORIES = {
  instrutor: [
    { value: "cartao_cidadao", label: "Cartão de Cidadão" },
    { value: "medical", label: "Certificado médico" },
    { value: "licenca_pu", label: "Licença PU (com a licença de instrutor no mesmo documento)" },
    { value: "profcheck", label: "Prova com examinador (ProfCheck)" },
    { value: "outro", label: "Outro" },
  ],
  aluno: [
    { value: "cartao_cidadao", label: "Cartão de Cidadão" },
    { value: "cartao_aluno", label: "Cartão de aluno" },
    { value: "medical", label: "Certificado médico" },
    { value: "outro", label: "Outro" },
  ],
  piloto: [
    { value: "medical", label: "Certificado médico" },
    { value: "licenca", label: "Licença (PU ou PPL)" },
    { value: "profcheck", label: "Prova com examinador (ProfCheck)" },
    { value: "outro", label: "Outro" },
  ],
  aircraft: [
    { value: "navegabilidade", label: "Navegabilidade" },
    { value: "radio", label: "Licença de rádio" },
    { value: "seguro", label: "Seguro" },
    { value: "outro", label: "Outro" },
  ],
};
const OBRIGATORIOS = {
  instrutor: new Set(["cartao_cidadao", "medical", "licenca_pu"]),
  aluno: new Set(["cartao_cidadao", "cartao_aluno", "medical"]),
  piloto: new Set(["medical", "licenca"]),
};
const TIPO_POR_OMISSAO = { instructor: "instrutor", pilot: "piloto", aircraft: "aircraft" };


function _fmtBytes(b) {
  if (!b) return "—";
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / 1024 / 1024).toFixed(1)} MB`;
}

function _fmtDate(s) {
  if (!s) return "—";
  return s.slice(0, 10);
}


/**
 * DocumentsManager
 *
 * Reusable upload+list panel for instructor / pilot / aircraft.
 *
 * Props:
 *   ownerKind: "instructor" | "pilot" | "aircraft"
 *   ownerId:   ID string
 *   compact:   if true, smaller heading (default false)
 */
export default function DocumentsManager({ ownerKind, ownerId, compact = false, tipo }) {
  const tipoEfetivo = tipo || TIPO_POR_OMISSAO[ownerKind] || ownerKind;
  const cats = CATEGORIES[tipoEfetivo] || [];
  const obrigatorios = OBRIGATORIOS[tipoEfetivo] || new Set();
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [category, setCategory] = useState((cats[0] || { value: "outro" }).value);
  useEffect(() => { setCategory((cats[0] || { value: "outro" }).value); }, [tipoEfetivo]);
  const [validUntil, setValidUntil] = useState("");
  const [validUntilInstrutor, setValidUntilInstrutor] = useState("");
  const [notes, setNotes] = useState("");
  // Instrutor: a licença de instrutor vem no mesmo documento da licença PU, com a sua própria validade
  const pedeLicencaInstrutor = tipoEfetivo === "instrutor" && category === "licenca_pu";
  const [viewing, setViewing] = useState(null);
  const fileInputRef = useRef(null);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/di/documents/by-owner/${ownerKind}/${ownerId}`, { withCredentials: true });
      setDocs(r.data || []);
    } catch (e) {
      toast.error(mensagemDeErro(e, "Erro a carregar documentos"));
    } finally {
      setLoading(false);
    }
  }, [ownerKind, ownerId]);

  useEffect(() => { reload(); }, [reload]);

  const onUpload = async (file) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Ficheiro demasiado grande (máx 10 MB).");
      return;
    }
    if (obrigatorios.has(category) && !validUntil) {
      toast.error("Indique a validade que consta no documento.");
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }
    if (pedeLicencaInstrutor && !validUntilInstrutor) {
      toast.error("Indique também a validade da licença de instrutor (está no mesmo documento).");
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }
    let copia;
    try {
      copia = await copiaDoFicheiro(file);   // no iPhone o ficheiro original pode chegar vazio ao servidor
    } catch (err) {
      toast.error(err.message);
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }
    const fd = new FormData();
    fd.append("file", copia, copia.name);
    fd.append("category", category);
    if (validUntil) fd.append("valid_until", validUntil);
    if (pedeLicencaInstrutor && validUntilInstrutor) fd.append("valid_until_instrutor", validUntilInstrutor);
    if (notes) fd.append("notes", notes);
    setUploading(true);
    try {
      await axios.post(
        `${API}/di/documents/${ownerKind}/${ownerId}`, fd,
        { withCredentials: true },   // sem Content-Type: o navegador põe o multipart com a boundary
      );
      toast.success("Documento carregado");
      setValidUntil("");
      setNotes("");
      if (fileInputRef.current) fileInputRef.current.value = "";
      reload();
    } catch (e) {
      toast.error(mensagemDeErro(e, "Erro a carregar ficheiro"));
    } finally {
      setUploading(false);
    }
  };

  const onDelete = async (doc) => {
    if (!window.confirm(`Eliminar "${doc.filename}"?`)) return;
    try {
      await axios.delete(`${API}/di/documents/${doc.id}`, { withCredentials: true });
      toast.success("Documento eliminado");
      reload();
    } catch (e) {
      toast.error(mensagemDeErro(e, "Erro a eliminar"));
    }
  };

  const downloadUrl = (doc) => `${API}/di/documents/${doc.id}/download`;

  return (
    <div className="border border-slate-700 rounded p-3 bg-slate-900/40 min-w-0" data-testid="di-docs-manager">
      <div className="flex items-center gap-2 mb-3">
        <Paperclip className="w-4 h-4 text-blue-300" />
        <h4 className={`font-semibold text-slate-100 ${compact ? "text-sm" : ""}`}>Documentos anexos</h4>
        <span className="text-[10px] text-slate-500">PDF / JPG / PNG · até 10 MB</span>
      </div>

      {/* Upload form */}
      <div className="grid grid-cols-1 sm:grid-cols-12 gap-2 text-xs mb-3">
        <div className="sm:col-span-3">
          <Label className="text-slate-400 text-[10px] flex min-h-[2rem] items-end leading-tight">Categoria</Label>
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger className="bg-slate-800 border-slate-700 h-9" data-testid="di-docs-category">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-slate-900 border-slate-700">
              {cats.map((c) => (
                <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="sm:col-span-3">
          <Label className="text-slate-400 text-[10px] flex min-h-[2rem] items-end leading-tight">{obrigatorios.has(category) ? "Validade (obrigatória)" : "Validade (opcional)"}</Label>
          <Input
            type="date"
            value={validUntil}
            onChange={(e) => setValidUntil(e.target.value)}
            className="bg-slate-800 border-slate-700 h-9"
            data-testid="di-docs-valid-until"
          />
        </div>
        {pedeLicencaInstrutor && (
          <div className="sm:col-span-3">
            <Label className="text-slate-400 text-[10px] flex min-h-[2rem] items-end leading-tight">Validade da licença de instrutor (obrigatória)</Label>
            <Input
              type="date"
              value={validUntilInstrutor}
              onChange={(e) => setValidUntilInstrutor(e.target.value)}
              className="bg-slate-800 border-slate-700 h-9"
              data-testid="di-docs-valid-until-instrutor"
            />
          </div>
        )}
        <div className="sm:col-span-6">
          <Label className="text-slate-400 text-[10px] flex min-h-[2rem] items-end leading-tight">Notas (opcional)</Label>
          <Input
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            className="bg-slate-800 border-slate-700 h-9"
            placeholder="Ex: emitido em..."
            data-testid="di-docs-notes"
          />
        </div>
        <div className="sm:col-span-12">
          <label className="flex items-center justify-center gap-2 px-3 py-2 rounded border border-dashed border-blue-500/40 bg-blue-500/5 hover:bg-blue-500/10 text-blue-300 cursor-pointer text-xs">
            <Upload className="w-4 h-4" />
            <span>{uploading ? "A carregar…" : "Escolher ficheiro e carregar"}</span>
            <input
              ref={fileInputRef}
              type="file"
              accept="application/pdf,image/jpeg,image/png,.pdf,.jpg,.jpeg,.png"
              className="hidden"
              disabled={uploading}
              onChange={(e) => onUpload(e.target.files?.[0])}
              data-testid="di-docs-file-input"
            />
          </label>
        </div>
      </div>

      {/* List */}
      {loading ? (
        <div className="text-slate-500 text-xs italic py-2">A carregar documentos…</div>
      ) : docs.length === 0 ? (
        <div className="text-slate-500 text-xs italic py-2" data-testid="di-docs-empty">
          Sem documentos anexos. Carrega um ficheiro acima.
        </div>
      ) : (
        <div className="space-y-1.5" data-testid="di-docs-list">
          {docs.map((d) => {
            const expired = d.valid_until && d.valid_until < new Date().toISOString().slice(0, 10);
            return (
              <div key={d.id} className="flex items-start gap-2 border border-slate-800 rounded p-2 bg-slate-900/60 text-xs min-w-0" data-testid="di-docs-item">
                <FileText className="w-4 h-4 text-slate-400 shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <div className="text-slate-100 truncate font-medium" title={d.filename}>
                    {d.filename}
                  </div>
                  <div className="flex flex-wrap items-center gap-1.5 mt-0.5">
                    <span className="text-[9px] uppercase px-1 py-0.5 rounded bg-slate-800 text-slate-400 tracking-wide">
                      {cats.find((c) => c.value === d.category)?.label || d.category}
                    </span>
                    {expired && (
                      <span className="inline-flex items-center gap-1 text-[9px] uppercase font-bold text-red-300 bg-red-500/20 px-1.5 py-0.5 rounded">
                        <AlertTriangle className="w-3 h-3" /> Expirado
                      </span>
                    )}
                  </div>
                  <div className="text-[10px] text-slate-500 flex flex-wrap gap-x-2 mt-0.5">
                    <span>{_fmtBytes(d.size_bytes)}</span>
                    {d.valid_until && <span>· Válido até {_fmtDate(d.valid_until)}</span>}
                    {d.uploaded_at && <span>· {_fmtDate(d.uploaded_at)}</span>}
                  </div>
                  {d.notes && (
                    <div className="text-[10px] text-slate-400 mt-0.5 italic break-words">{d.notes}</div>
                  )}
                </div>
                <button
                  onClick={() => setViewing(d)}
                  className="p-1.5 rounded hover:bg-slate-800 text-slate-400 hover:text-blue-300"
                  title="Ver na app"
                  data-testid={`di-docs-view-${d.id}`}
                >
                  <Eye className="w-3.5 h-3.5" />
                </button>
                <a
                  href={downloadUrl(d)}
                  download={d.filename}
                  className="p-1.5 rounded hover:bg-slate-800 text-slate-400 hover:text-blue-300"
                  title="Descarregar"
                  data-testid={`di-docs-download-${d.id}`}
                >
                  <Download className="w-3.5 h-3.5" />
                </a>
                <button
                  onClick={() => onDelete(d)}
                  className="p-1.5 rounded hover:bg-slate-800 text-slate-400 hover:text-red-400"
                  title="Eliminar"
                  data-testid={`di-docs-delete-${d.id}`}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            );
          })}
        </div>
      )}

      {uploading && (
        <Button
          variant="ghost"
          size="sm"
          disabled
          className="mt-2 w-full text-xs text-slate-400"
        >
          A carregar…
        </Button>
      )}

      {viewing && (
        <DocumentViewer
          doc={viewing}
          downloadUrl={`${API}/di/documents/${viewing.id}/download`}
          onClose={() => setViewing(null)}
        />
      )}
    </div>
  );
}
